import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { AlertTriangle, ArrowDownUp, CheckCircle } from "lucide-react";

export default function DisposalsPage() {
  const { activeEntity, categories, getCategoryName } = useApp();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().substring(0, 10));
  const [reason, setReason] = useState("SOLD");
  const [proceeds, setProceeds] = useState(0);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [disposals, setDisposals] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { status: "ACTIVE", page_size: 1000 };
        if (activeEntity) params.entity_id = activeEntity.id;
        const { data } = await api.get("/assets", { params });
        setAssets(data.assets);
        // Also load disposed assets
        const params2 = { status: "DISPOSED", page_size: 100 };
        if (activeEntity) params2.entity_id = activeEntity.id;
        const { data: d2 } = await api.get("/assets", { params: params2 });
        setDisposals(d2.assets);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [activeEntity]);

  const previewDisposal = async () => {
    if (!selectedAsset) return;
    try {
      const { data } = await api.post("/disposals/preview", {
        asset_id: selectedAsset.id, disposal_date: disposalDate, reason, proceeds: parseFloat(proceeds) || 0
      });
      setPreview(data);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const postDisposal = async () => {
    if (!selectedAsset || !preview) return;
    if (!window.confirm(`Post disposal of ${selectedAsset.tag}?\n${preview.is_profit ? "Profit" : "Loss"}: ${formatCurrency(Math.abs(preview.profit_loss))}`)) return;
    setPosting(true);
    try {
      await api.post("/disposals", {
        asset_id: selectedAsset.id, disposal_date: disposalDate, reason, proceeds: parseFloat(proceeds) || 0
      });
      alert("Disposal posted successfully!");
      setShowForm(false); setSelectedAsset(null); setPreview(null);
      // Reload
      const params = { status: "ACTIVE", page_size: 1000 };
      if (activeEntity) params.entity_id = activeEntity.id;
      const { data } = await api.get("/assets", { params });
      setAssets(data.assets);
    } catch (e) { alert(e.response?.data?.detail || "Error posting disposal"); }
    finally { setPosting(false); }
  };

  return (
    <div className="space-y-6" data-testid="disposals-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Disposals</h1>
        <p className="text-sm text-[#6B7280] mt-1">Dispose of assets and generate derecognition journals</p>
      </div>

      {!showForm ? (
        <>
          <div className="stratum-card p-6">
            <h3 className="font-display text-lg font-semibold mb-4">Select Asset to Dispose</h3>
            <select className="stratum-input w-full max-w-lg" onChange={(e) => { const a = assets.find(x => x.id === e.target.value); setSelectedAsset(a); }} data-testid="disposal-asset-select">
              <option value="">Choose an asset...</option>
              {assets.map(a => (
                <option key={a.id} value={a.id}>{a.tag} - {a.description} (Carrying: {formatCurrency(a.carrying_amount)})</option>
              ))}
            </select>
            {selectedAsset && (
              <button onClick={() => setShowForm(true)} className="stratum-btn-primary mt-4" data-testid="start-disposal-btn">
                Dispose {selectedAsset.tag}
              </button>
            )}
          </div>

          {/* Disposal History */}
          {disposals.length > 0 && (
            <div className="stratum-card overflow-hidden">
              <div className="p-4 border-b border-[#E8E2D5]">
                <h3 className="font-display text-lg font-semibold">Disposal Log</h3>
              </div>
              <table className="w-full stratum-table" data-testid="disposal-log-table">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Description</th>
                    <th>Disposal Date</th>
                    <th>Reason</th>
                    <th className="text-right">Proceeds</th>
                    <th className="text-right">P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {disposals.map(d => (
                    <tr key={d.id}>
                      <td className="font-mono text-sm">{d.tag}</td>
                      <td className="text-sm">{d.description}</td>
                      <td className="text-sm font-mono tabular-nums">{formatDate(d.disposal_date)}</td>
                      <td className="text-xs">{d.disposal_reason}</td>
                      <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(d.disposal_proceeds)}</td>
                      <td className={`text-right font-mono tabular-nums text-sm font-medium ${(d.disposal_profit_loss || 0) >= 0 ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                        {formatCurrency(d.disposal_profit_loss)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="max-w-2xl space-y-6">
          <div className="stratum-card p-6">
            <h3 className="font-display text-lg font-semibold mb-4">
              <ArrowDownUp size={18} className="inline mr-2 text-[#C8A961]" />
              Dispose: {selectedAsset.tag}
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-6 text-sm">
              <div><span className="text-[#6B7280]">Description:</span> <span className="font-medium">{selectedAsset.description}</span></div>
              <div><span className="text-[#6B7280]">Category:</span> <span className="font-medium">{getCategoryName(selectedAsset.category_id)}</span></div>
              <div><span className="text-[#6B7280]">Cost:</span> <span className="font-mono tabular-nums">{formatCurrency(selectedAsset.cost)}</span></div>
              <div><span className="text-[#6B7280]">Accum Dep:</span> <span className="font-mono tabular-nums">{formatCurrency(selectedAsset.accumulated_depreciation)}</span></div>
              <div><span className="text-[#6B7280]">Carrying:</span> <span className="font-mono tabular-nums font-semibold">{formatCurrency(selectedAsset.carrying_amount)}</span></div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Disposal Date</label>
                <input type="date" className="stratum-input" value={disposalDate} onChange={(e) => setDisposalDate(e.target.value)} data-testid="disposal-date-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Reason</label>
                <select className="stratum-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="disposal-reason-select">
                  <option value="SOLD">Sold</option>
                  <option value="SCRAPPED">Scrapped</option>
                  <option value="DONATED">Donated</option>
                  <option value="LOST">Lost</option>
                  <option value="WRITTEN_OFF">Written off</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Proceeds (R)</label>
                <input type="number" className="stratum-input font-mono" value={proceeds} onChange={(e) => setProceeds(e.target.value)} data-testid="disposal-proceeds-input" />
              </div>
              <button onClick={previewDisposal} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4] font-medium" data-testid="preview-disposal-btn">
                Preview Journal
              </button>
            </div>
          </div>

          {preview && (
            <div className={`stratum-card p-6 ${preview.is_profit ? "border-l-4 border-l-[#065F46]" : "border-l-4 border-l-[#991B1B]"}`}>
              <div className="flex items-center gap-2 mb-4">
                {preview.is_profit ? <CheckCircle size={18} className="text-[#065F46]" /> : <AlertTriangle size={18} className="text-[#991B1B]" />}
                <span className={`text-lg font-display font-semibold ${preview.is_profit ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                  {preview.is_profit ? "Profit" : "Loss"} on Disposal: {formatCurrency(Math.abs(preview.profit_loss))}
                </span>
              </div>

              <h4 className="text-sm font-medium mb-3">Derecognition Journal Preview</h4>
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-medium text-[#6B7280] pb-2 border-b border-[#E8E2D5]">
                  <span className="flex-1">Account</span>
                  <span className="w-32 text-right">Debit</span>
                  <span className="w-32 text-right">Credit</span>
                </div>
                {preview.journal_lines?.map((line, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5">
                    <span className="flex-1">{line.account}</span>
                    <span className="w-32 text-right font-mono tabular-nums">{line.debit > 0 ? formatCurrency(line.debit) : ""}</span>
                    <span className="w-32 text-right font-mono tabular-nums">{line.credit > 0 ? formatCurrency(line.credit) : ""}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#E8E2D5]">
                <span className={`text-xs ${preview.is_balanced ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                  {preview.is_balanced ? "Journal is balanced" : "WARNING: Journal is not balanced"}
                </span>
                <div className="flex gap-3">
                  <button onClick={() => { setShowForm(false); setPreview(null); }} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm" data-testid="cancel-disposal-btn">Cancel</button>
                  <button onClick={postDisposal} disabled={posting || !preview.is_balanced} className="stratum-btn-primary disabled:opacity-50" data-testid="post-disposal-btn">
                    {posting ? "Posting..." : "Post Disposal"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
